import { WidgetConfigFadeFeatureEnum,
         WidgetInjectionTypeEnum,
         WidgetConfigEntity }                         from '..';
import fr from './fr';
import en from './en';


/**
 * The service that provide easy access to configuration parameters encapsulating the origin, default 
 * values or the business logic related to the override hierarchy.
 */
export class ConfigService {
   
   /** The HTML element ID to provide at standard integration mode */
   public static GENERIC_INJECTION_ELEM_ID : string = 'synapse-chatbox-app';

   /** ******************************************************************************************* */
   /**                                      IFAQ CONFIG PARAMS                                     */
   /** ******************************************************************************************* */

   public static widgetConfig: WidgetConfigEntity | undefined = undefined;
   public static faqId: string | undefined = undefined;
   public static botKey: string | undefined = undefined;
   public static synapseBotId: string | undefined = undefined;
   public static userName: string | undefined = undefined;
   public static roleName: string | undefined = undefined;
   public static roleValue: string | undefined = undefined;
   public static isEmbeddedModeRequested: boolean | undefined = undefined;
   public static isLogConversationActivated: boolean = true;
   public static lang: string = "fr";
   
   public static setWidgetConfigEntity(config: WidgetConfigEntity) {
      ConfigService.widgetConfig = config;
   }

   public static getBreakpointThresholds(): any | null {
      return (window.botSettings && window.botSettings.breakpoint) ? window.botSettings.breakpoint.thresholds 
                                                                   : null;
   }

   public static overrideIFaqWidgetConfigParams() {
      if (window.botSettings) {
         if (!window.botSettings.customChatbotTheme && ConfigService.widgetConfig && ConfigService.widgetConfig.params) {
            const customChatbotTheme: string = ConfigService.widgetConfig.params.get('customChatbotTheme');
            if (customChatbotTheme) {
               try {
                  window.botSettings.customChatbotTheme = JSON.parse(customChatbotTheme);
               } catch (err) {
                  window.botSettings.customChatbotTheme = {};
                  alert('Invalid custom theme configured in database. No theme will be used, a valid JSON objet shall be configured in customChatbotTheme parameter.');
               }
            }
         }

         Object.getOwnPropertyNames(window.botSettings).forEach((setting: string) => {
            if (ConfigService.widgetConfig && ConfigService.widgetConfig.params && window.botSettings[setting]) {
               ConfigService.widgetConfig.params.set(setting, window.botSettings[setting]);
            }
         });
      }
   }

   public static initSuggestedQuestionsList(suggestedQuestions: string[]) {
      if (ConfigService.widgetConfig) {
         ConfigService.widgetConfig.suggestedQuestions = suggestedQuestions;
      }
   }

   public static getSuggestedQuestionsList(): string[] | undefined {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.suggestedQuestions) {
        return ConfigService.widgetConfig.suggestedQuestions;
      }
      return undefined;
   }

   /** ******************************************************************************************* */
   /**                                 ENVIRONMENT CONFIG PARAMS                                   */
   /** ******************************************************************************************* */

   public static getMediaRequestTimeout(): number {
      return (process.env.MEDIA_REQUEST_TIMEOUT === undefined) ? 3000 : Number(process.env.MEDIA_REQUEST_TIMEOUT);
   }

   public static getDefaulRequestTimeout(): number {
      return (process.env.DEFAULT_REQUEST_TIMEOUT === undefined) ? 0 : Number(process.env.DEFAULT_REQUEST_TIMEOUT);
   }
   
   public static getPoolBotAnswerRequestPeriod(): number {
      return (process.env.DEFAULT_BOT_ANSWER_REQUEST_PERIOD === undefined) ? 2000 : Number(process.env.DEFAULT_BOT_ANSWER_REQUEST_PERIOD);
   }

   public static getPoolBotAnswerRequestNbRetries(): number {
      return (process.env.DEFAULT_BOT_ANSWER_REQUEST_NB_RETRIES === undefined) ? 5 : Number(process.env.DEFAULT_BOT_ANSWER_REQUEST_NB_RETRIES);
   }
   
   public static getFakeBotAnswerWaitingTime(): number {
      return (process.env.FAKE_BOT_ANSWER_WAITING_TIME === undefined) ? 500 : Number(process.env.FAKE_BOT_ANSWER_WAITING_TIME);
   }
   
   public static getBotUserOrigin(): string {
      return 'botuser'; // TODO add environment variable
   }
   
   /** ******************************************************************************************* */
   /**                                      SERVER CONFIG PARAMS                                   */
   /** ******************************************************************************************* */
   
   public static getNotificationPollingPeriod(): number {
      return 300000; // 5min
   }

   public static getDocPathRootURL(): string {
      return (window.serverConfig === undefined) ? '/' : window.serverConfig.docPathRoot;
   }

   public static getImgPathRootURL(): string {
      return (window.serverConfig === undefined) ? '/' : window.serverConfig.imgPathRoot;
   }

   public static getServerChatbotUiRootURL(): string {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params.get('synapseWidgetRoot')) {
         return ConfigService.widgetConfig.params.get('synapseWidgetRoot');
      }
      return (window.botSettings && window.botSettings.synapseWidgetRoot) ? window.botSettings.synapseWidgetRoot : '';
   }

   public static getIntelligentFaqRootURL(): string {
      return (window.serverConfig !== undefined) ? window.serverConfig.restRoot : '/IntelligentFaq/';
   }

   public static getBotServerRootURI(): string {
         if (window.serverConfig !== undefined && window.serverConfig.botRoot) {
            if (window.serverConfig.botRoot.indexOf('localhost:') !== -1) {
               return window.serverConfig.botRoot
            } else {
               if (ConfigService.widgetConfig && ConfigService.widgetConfig.params) {
                  let synapseBotRoot: string = ConfigService.widgetConfig.params.get('synapseBotRoot') ? ConfigService.widgetConfig.params.get('synapseBotRoot') : '';
                  if (synapseBotRoot.indexOf('line/synapse') !== -1) {
                     synapseBotRoot = synapseBotRoot.substring(0, synapseBotRoot.indexOf('line/synapse'));
                  }
                  if (synapseBotRoot.indexOf('http') !== -1) {
                     return synapseBotRoot;
                  }
                  return synapseBotRoot.length > 0 ? `${window.serverConfig.botRoot}${synapseBotRoot}` : `${window.serverConfig.botRoot}/BotSynapseStandard/`;
               } else {
                  return `${window.serverConfig.botRoot}/BotSynapseStandard/`
               }
            }
         } else {
            return 'http://localhost/BotSynapseStandard/'
         }
   }
   
   /** ******************************************************************************************* */
   /**                                 INTEGRATION SETTINGS CONFIG                                 */
   /** ******************************************************************************************* */

   public static getIntegrationTagId(): string {
      return (window.botSettings && window.botSettings.htmlElemIdForIntegration !== undefined)  ? window.botSettings.htmlElemIdForIntegration
                                                                                                : ConfigService.GENERIC_INJECTION_ELEM_ID;
   }

   public static isExternalIntegration(): boolean {
      return (window.botSettings && window.botSettings.htmlElemIdForIntegration !== undefined && !window.botSettings.showExtIntegration);
   }

   public static hideCloseButton(): boolean {
      return (window.botSettings && window.botSettings.showCloseButton !== undefined && !window.botSettings.showCloseButton)
   }

   public static setBotCredentials(faqId: string, botKey: string, synapseBotId: string, roleValue?: string, lang: string = 'fr') {
      ConfigService.faqId = faqId;
      ConfigService.botKey = botKey;
      ConfigService.synapseBotId = synapseBotId;
      ConfigService.roleValue = roleValue ? roleValue : '';
      ConfigService.isLogConversationActivated = ConfigService.setConversationLogsRecordMode();
      ConfigService.lang = lang;
      if (!window.botSettings) { window.botSettings = {} }
   }

   public static setWidgetMode(isEmbedded: boolean) {
      ConfigService.isEmbeddedModeRequested = isEmbedded;
   }
   
   public static setConversationLogsRecordMode(): boolean {
      // V19.3.2 - https://synapsedeveloppement.atlassian.net/browse/CHATBOT-4162
      // Old conditional return : return (ConfigService.roleValue === '' || ConfigService.roleValue === 'chatbot.user' ? true : false)
      // V19.3.3 - Due to a lack of specification in CHATBOT-4162, this method will now return true 
      return true;
   }

   public static isEmbeddedModeConfigured(): boolean {
      if (ConfigService.isEmbeddedModeRequested !== undefined) {
         return ConfigService.isEmbeddedModeRequested;
      }
      return (window.botSettings && ((window.botSettings.integrationMode && window.botSettings.integrationMode === WidgetInjectionTypeEnum.INTEGRATED) || 
                                      window.botSettings.htmlElemIdForIntegration !== undefined));
   }

   public static getLanguageToUse(configLanguage: string | undefined): string {
      if (window.botSettings && window.botSettings.language) {
         return window.botSettings.language;
      }
      return (!configLanguage) ? 'fr' : configLanguage;
   }
   
   public static getConfiguredGuestId(): string | undefined {
      return (window.botSettings && window.botSettings.userId) ? window.botSettings.userId : undefined;
   }
   
   public static getElementClassOrIdToHideInFullScreenMode(): string | undefined {
      return (window.botSettings && window.botSettings.htmlElemToHideInFullScreen !== undefined)   ? window.botSettings.htmlElemToHideInFullScreen 
                                                                                                   : undefined
   }

   public static getElementClassOrIdToHideInFullScreenModePrefixed(): string | undefined {
      return (window.botSettings && window.botSettings.htmlElemToHideInFullScreen !== undefined)   ? "synapse-" + window.botSettings.htmlElemToHideInFullScreen 
                                                                                                   : undefined
   }

   /** ******************************************************************************************* */
   /**                                IFAQ CONFIG GETTERS PARAMS                                   */
   /** ******************************************************************************************* */

   public static getHeaderWellcomeTextContent(): string | undefined {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params && ConfigService.widgetConfig.params.get('welcomeBackMessageContent')) {
         return ConfigService.widgetConfig.params.get('welcomeBackMessageContent');
      }
      return '';
   }

   public static getHeaderTextContent(): string | undefined {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params && ConfigService.widgetConfig.params.get('botName')) {
         return ConfigService.widgetConfig.params.get('botName');
      }
      return '';
   }

   public static getBotImage(): string {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params) {
         const botImage: string | undefined = ConfigService.widgetConfig.params.get('botImage');
         if (botImage) {
            return (botImage.indexOf('http') === -1 && window.serverConfig) ? `${window.serverConfig.imgPathRoot}${botImage}` : `${botImage}`;
         }
      }
      return `${window.serverConfig.imgPathRoot}imgs/chatbot/swappy.png`;
   }

   public static getBotImagePresentation(): string { 
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params) {
         const botImage: string | undefined = ConfigService.widgetConfig.params.get('botImagePresentation');
         if (botImage) {
            return (botImage.indexOf('http') === -1 && window.serverConfig) ? `${window.serverConfig.imgPathRoot}${botImage}` : `${botImage}`;
         }
      }
      return `${window.serverConfig.imgPathRoot}imgs/chatbot/swappy_big.png`;
   }

   public static getLinkButtonImage(): string { 
      return `${window.serverConfig.imgPathRoot}imgs/chatbot/globe.svg`;
   }

   public static getDocButtonImage(): string { 
      return `${window.serverConfig.imgPathRoot}imgs/chatbot/file.svg`;
   }

   public static getEmotionAvatarFormat(): string | null {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params && ConfigService.widgetConfig.params.get('emoAvatarFormat')) {
         return ConfigService.widgetConfig.params.get('emoAvatarFormat') === '' ? null : ConfigService.widgetConfig.params.get('emoAvatarFormat').toLowerCase();
      }
      return null;
   }

   public static getStaticEmotionAvatarPlayTime(): number {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params && ConfigService.widgetConfig.params.get('emoAvatarPlayTime')) {
         return ConfigService.widgetConfig.params.get('emoAvatarPlayTime') >= 2000 ? 3000 : ConfigService.widgetConfig.params.get('emoAvatarPlayTime');
      }
      return 3000;
   }

   public static getBotResourceURI(): string {
      let botImageURI: string = this.getBotImage().split('.').slice(0, -1).join('.');
      if (botImageURI.indexOf('http') === -1) {
         botImageURI = `${ConfigService.getServerChatbotUiRootURL()}${botImageURI}`;
      }
      return botImageURI;
   }

   public static getExperimentalMode(): boolean {
      let isExperimentalModeActivated: boolean = false;
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params && ConfigService.widgetConfig.params.get('experimentation')) {
         isExperimentalModeActivated = ConfigService.widgetConfig.params.get('experimentation') === 'False' ? false : true;
      }
      return isExperimentalModeActivated;
   }

   /**  *****************************************  INITIALIZATION PARAMS    **************************************  */

   public static getBotNotificationCommand(): string {
      return '/notification';
   }
   
   public static getCustomCSSFile(): string | null {
      if (ConfigService.widgetConfig   && ConfigService.widgetConfig
                                       && ConfigService.widgetConfig.params
                                       && ConfigService.widgetConfig.params.get('cssCustom')) {
         
         const customCssFile: string | undefined = ConfigService.widgetConfig.params.get('cssCustom');
         if (customCssFile) {
            return (customCssFile.indexOf('http') !== -1) ? customCssFile 
                                                          : `${ConfigService.getServerChatbotUiRootURL()}${customCssFile}`;
         }
      }
      return null;
   }


   public static getCustomChatroomCSSFile(): string | null {
      if (ConfigService.widgetConfig   && ConfigService.widgetConfig
                                       && ConfigService.widgetConfig.params
                                       && ConfigService.widgetConfig.params.get('customChatroomCss')) {
         
         const customChatroomCssFile: string | undefined = ConfigService.widgetConfig.params.get('customChatroomCss');
         if (customChatroomCssFile) {
            return (customChatroomCssFile.indexOf('http') !== -1) ? customChatroomCssFile 
                                                                  : `${ConfigService.getServerChatbotUiRootURL()}${customChatroomCssFile}`;
         }
      }
      return null;
   }

   public static getSynapseClassToHideScrollbar(): string {
      return "synapse-hide-scroll";
   }
   
   /**  **********************************************    TEXTES PARAMS    *****************************************  */

   public static getDefaultThankFeedbackMessage(): string {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params 
                                     && ConfigService.widgetConfig.params.get('thankFeedbackMessage')) {
         return ConfigService.widgetConfig.params.get('thankFeedbackMessage');
      }
      return '';
   }

   public static getDefaultFeedbackMessage(): string {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params 
                                     && ConfigService.widgetConfig.params.get('feedbackConversationContent')) {
         return ConfigService.widgetConfig.params.get('feedbackConversationContent');
      }
      return '';
   }

   public static getMessageTyperPlaceholder(): string {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params 
                                     && ConfigService.widgetConfig.params.get('placeholder')) {
         return ConfigService.widgetConfig.params.get('placeholder');
      }
      return '';
   }

   public static getIntroductionSentence(): string | null {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params) {
         return ConfigService.widgetConfig.params.get('introductorySentenceContent') ? ConfigService.widgetConfig.params.get('introductorySentenceContent') : '';
      }
      return null;
   }

   public static getKnowMoreLinkLabel(): string {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params 
                                     && ConfigService.widgetConfig.params.get('sourceDocText')) {
         return ConfigService.widgetConfig.params.get('sourceDocText');
      }
      return 'Source';
   }

   public static getMessageBoxPlaceholderButtonLabel(): string {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params 
                                     && ConfigService.widgetConfig.params.get('botMessagePlaceholderBtnText')) {
         return ConfigService.widgetConfig.params.get('botMessagePlaceholderBtnText');
      }
      return '';
   }

   public static getMessageBoxPlaceholderIntroText(): string {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params 
                                     && ConfigService.widgetConfig.params.get('botMessagePlaceholderIntroText')) {
         return ConfigService.widgetConfig.params.get('botMessagePlaceholderIntroText');
      }
      return '';
   }

   public static getNetworkFailureMessage(): string {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params 
                                     && ConfigService.widgetConfig.params.get('networkFailureMessage')) {
         return ConfigService.widgetConfig.params.get('networkFailureMessage');
      }
      return '';
   }

   public static getErrorMessage(): string {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params 
                                     && ConfigService.widgetConfig.params.get('errorMessage')) {
         return ConfigService.widgetConfig.params.get('errorMessage');
      }
      return '';
   }

   public static getWelcomeBannerMessage(): string {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params 
                                     && ConfigService.widgetConfig.params.get('title')) {
         return ConfigService.widgetConfig.params.get('title');
      }
      return '';
   }

   public static showWelcomeBannerMessage(): boolean {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params
                                     && ConfigService.widgetConfig.params.get('isInfoOngletDisplayedOnMobile')) {
         return true;
      }
      return false;
   }

   public static getPresentationPanelContent(): string {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params 
                                     && ConfigService.widgetConfig.params.get('customChatroomHtmlContent')) {
         return ConfigService.widgetConfig.params.get('customChatroomHtmlContent');
      } else if (window.botSettings && window.botSettings.customChatroomHtmlContent) {
         return window.botSettings.customChatroomHtmlContent;
      }
      return '';
   }

   public static getExperimentalModeTexte(): string {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params 
                                     && ConfigService.widgetConfig.params.get('experimentationAlert')) {
         return ConfigService.widgetConfig.params.get('experimentationAlert');
      } 
      return '';
   }

   public static getDefaultPanelTextFontSize(): number | undefined {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params 
                                     && ConfigService.widgetConfig.params.get('panelTextFontSize')
                                     ) {
         const fontsize =  ConfigService.widgetConfig.params.get('panelTextFontSize');
         return fontsize * 16; // rem to px
      }
      return undefined;
   }

   public static getLimitCharacterErrorText(): string {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params 
                                     && ConfigService.widgetConfig.params.get('limitCharacterErrorText')) {
         return ConfigService.widgetConfig.params.get('limitCharacterErrorText');
      } 
      return 'Les messages sont limités à 300 caractères';
   }

   public static getLimitCharacterErrorPaste() : string{
      return 'Le message copié excede la taille limite de 300 caractères';
   }

   public static getLimitMessageCharactere(): Number {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params 
                                     && ConfigService.widgetConfig.params.get('limitMessageCharactere')) {
         return ConfigService.widgetConfig.params.get('limitMessageCharactere');
      } 
      return 300;
   }

   public static getDeleteConversationBtnText(): string {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params 
                                     && ConfigService.widgetConfig.params.get('deleteConversationBtnText')) {
         return ConfigService.widgetConfig.params.get('deleteConversationBtnText');
      }
      return 'Relancer la conversation';
   }

   public static getCopyAlertSuccessfulText(): string {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params 
                                     && ConfigService.widgetConfig.params.get('copyAlertSuccessfulText')) {
         return ConfigService.widgetConfig.params.get('copyAlertSuccessfulText');
      }
      return 'La réponse a été copiée avec succès !';
   }

   public static getCopyFailedAlertText(): string {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params 
                                     && ConfigService.widgetConfig.params.get('copyAlertFailedText')) {
         return ConfigService.widgetConfig.params.get('copyAlertFailedText');
      }
      return 'Échec de la copie de la réponse !';
   }

   /**  *****************************************    TRUNCATE MESSAGE PARAMS    ***************************************  */

   public static getMinMessageHeight(): number {
      return (process.env.DEFAULT_MIN_BOT_ANSWER_SIZE === undefined) ? 150 : Number(process.env.DEFAULT_MIN_BOT_ANSWER_SIZE);
   }

   public static getMaxMessageHeight(): number {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params 
                                     && ConfigService.widgetConfig.params.get('truncateMaxMessageSize')) {
         return ConfigService.widgetConfig.params.get('truncateMaxMessageSize');
      }
      // default value is not to have a max message size
      return 0;
   }

   public static getTruncatePolicy(): string | undefined {
      if (ConfigService.getMaxMessageHeight() < 0) { return; } // do not return a policy if no max message size configured

      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params
                                     && ConfigService.widgetConfig.params.get('truncatePolicy')) {
         return ConfigService.widgetConfig.params.get('truncatePolicy');
      }
      // default value is to display the complete message
      return WidgetConfigFadeFeatureEnum.UNFOLDABLE; 
   }

   public static getMaxBotOptionsAllowed(): number {
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params
                                     && ConfigService.widgetConfig.params.get('truncateBotOptions')) {
         return ConfigService.widgetConfig.params.get('truncateBotOptions');
      }
      return (process.env.DEFAULT_MAX_BOT_OPTIONS_ALLOWED !== undefined) ? Number(process.env.DEFAULT_MAX_BOT_OPTIONS_ALLOWED) : 5;
   }

   /**  *****************************************    NOTIFICATION BOX PARAMS    ***************************************  */

   public static getNotificationBoxStyle(): any{
      if (ConfigService.widgetConfig && ConfigService.widgetConfig.params && ConfigService.widgetConfig.params.get('notificationBoxStyle')) {
         return JSON.parse(ConfigService.widgetConfig.params.get('notificationBoxStyle'))
      } 
      return {ratio : 0.429 ,  widthFullScreen: {vwWidth : 70, pxWidth: 480} , widthFloat : {vwWidth : 35, pxWidth: 480}};
   }

   /**  *****************************************    BUTTON TITLE PARAMS    ***************************************  */

   // Define a method to get the correct language object
   private static getLangResource(): Record<string, string> {
      return this.lang === 'fr' ? fr : en;
   }

   // Define the methods to get the button titles
   public static getCopyButtonTitle(): string {
      const resource = this.getLangResource();
      return resource['app.button.title.copy'];
   }

   public static getSatisfactionButtonTitle(): string {
      const resource = this.getLangResource();
      return resource['app.button.title.satisfaction'];
   }

   public static getDissatisfactionButtonTitle(): string {
      const resource = this.getLangResource();
      return resource['app.button.title.dissatisfaction'];
   }

   public static getSendSatisfactionButtonTitle(): string {
      const resource = this.getLangResource();
      return resource['app.button.title.sendSatisfaction'];
   }

   public static getSendDissatisfactionButtonTitle(): string {
      const resource = this.getLangResource();
      return resource['app.button.title.sendDissatisfaction'];
   }

   public static getSourceButtonTitle(): string {
      const resource = this.getLangResource();
      return resource['app.button.title.source'];
   }
   
   public static getCloseNotificationButtonTitle(): string {
      const resource = this.getLangResource();
      return resource['app.button.title.closeNotification'];
   }

   public static getSendButtonTitle(): string {
      const resource = this.getLangResource();
      return resource['app.button.title.send'];
   }

   public static getRemoveButtonTitle(): string {
      const resource = this.getLangResource();
      return resource['app.button.title.remove'];
   }

   public static getSettingsButtonTitle(): string {
      const resource = this.getLangResource();
      return resource['app.button.title.settings'];
   }

   public static getOpenFeedbackButtonTitle(): string {
      const resource = this.getLangResource();
      return resource['app.button.title.openFeedback'];
   }

   public static getCloseFeedbackButtonTitle(): string {
      const resource = this.getLangResource();
      return resource['app.button.title.closeFeedback'];
   }

   public static getOpenButtonTitle(): string {
      const resource = this.getLangResource();
      return resource['app.button.title.openChat'];
   }

   public static getCloseButtonTitle(): string {
      const resource = this.getLangResource();
      return resource['app.button.title.closeChat'];
   }

   public static getCloseDrawerButtonTitle(): string {
      const resource = this.getLangResource();
      return resource['app.button.title.closeDrawer'];
   }

   public static getOpenNotificationButtonTitle(): string {
      const resource = this.getLangResource();
      return resource['app.button.title.openNotification'];
   }

   public static getFileQuoteButtonTitle(): string {
      const resource = this.getLangResource();
      return resource['app.button.title.fileQuote'];
   }

   public static getWebSiteQuoteButtonTitle(): string {
      const resource = this.getLangResource();
      return resource['app.button.title.webSiteQuote'];
   }

   public static getExternalWebSiteQuoteButtonTitle(): string {
      const resource = this.getLangResource();
      return resource['app.button.title.externalWebSiteQuote'];
   }

   public static getNewWindowButtonLabel(): string {
      const resource = this.getLangResource();
      return resource['app.button.label.newwindow'];
   }

   public static getDownloadButtonLabel(): string {
      const resource = this.getLangResource();
      return resource['app.button.label.download'];
   }

   public static getGoToStartOfConversationBtnText(): string {
      const resource = this.getLangResource();
      return resource['app.button.label.goToStartOfConversation'];
   }

   public static getAvatarImgAltNeutral(): string {
      const resource = this.getLangResource();
      return resource['app.img.alt.avatar.neutral'];
   }

   public static getAvatarImgAltPresentation(): string {
      const resource = this.getLangResource();
      return resource['app.img.alt.avatar.presentation'];
   }

   public static getAvatarImgAltThink(): string {
      const resource = this.getLangResource();
      return resource['app.img.alt.avatar.think'];
   }

   public static getAvatarImgAltJoy(): string {
      const resource = this.getLangResource();
      return resource['app.img.alt.avatar.joy'];
   }

   public static getAvatarImgAltSadness(): string {
      const resource = this.getLangResource();
      return resource['app.img.alt.avatar.sadness'];
   }

   public static getHiddenTextUser(): string {
      const resource = this.getLangResource();
      return resource['app.hidden.text.user'];
   }

   public static getHiddenTextBot(): string {
      const resource = this.getLangResource();
      return resource['app.hidden.text.bot'];
   }

   public static getHiddenTextExplainBot(): string {
      const resource = this.getLangResource();
      return resource['app.hidden.text.explain.bot'];
   }

   public static getHiddenTextProgressBarLoading(): string {
      const resource = this.getLangResource();
      return resource['app.hidden.text.progressbar.loading'];
   }

   public static getHiddenTextProgressBarLoaded(): string {
      const resource = this.getLangResource();
      return resource['app.hidden.text.progressbar.loaded'];
   }

   public static getHiddenTextDissatisfaction(): string {
      const resource = this.getLangResource();
      return resource['app.hidden.text.dissatisfaction'];
   }
}
